import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getEffectiveApiUrl } from '../config/runtimeEnv';
import { clearPersistedAuth, readPersistedAuthJson } from '../store/authStore';
import { useContextStore } from '../store/contextStore';
import { getSsoToken } from './ssoTokenProvider';

/**
 * Base URL for the API. Set via VITE_API_URL in .env or at build time; see runtimeEnv.ts.
 */
const API_BASE_URL = getEffectiveApiUrl();

/**
 * Decodes the payload of a JWT without verifying its signature. Returns null when the token
 * is missing/opaque/malformed. Used only for client-side diagnostics (e.g. explaining a 401).
 */
function decodeJwtPayload(token: string | null | undefined): Record<string, unknown> | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null; // not a JWT (e.g. opaque API key)
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Builds a human-readable summary of a JWT for logging: audience, issuer, scopes, and expiry
 * status. Never logs the raw token or signature.
 */
function summarizeToken(token: string | null | undefined): Record<string, unknown> {
  if (!token) return { present: false };
  const claims = decodeJwtPayload(token);
  if (!claims) {
    return { present: true, kind: 'opaque/non-jwt', length: token.length };
  }
  const nowSec = Math.floor(Date.now() / 1000);
  const exp = typeof claims.exp === 'number' ? claims.exp : undefined;
  const iat = typeof claims.iat === 'number' ? claims.iat : undefined;
  const nbf = typeof claims.nbf === 'number' ? claims.nbf : undefined;
  return {
    present: true,
    kind: 'jwt',
    aud: claims.aud,
    iss: claims.iss,
    // B2C access tokens carry scp/scope; ID tokens usually do not.
    scp: claims.scp ?? claims.scope,
    tfp: claims.tfp ?? claims.acr, // B2C user flow / policy
    sub: claims.sub,
    oid: claims.oid,
    tid: claims.tid,
    isExpired: exp !== undefined ? exp <= nowSec : undefined,
    notYetValid: nbf !== undefined ? nbf > nowSec : undefined,
    expiresInSec: exp !== undefined ? exp - nowSec : undefined,
    issuedAt: iat !== undefined ? new Date(iat * 1000).toISOString() : undefined,
    expiresAt: exp !== undefined ? new Date(exp * 1000).toISOString() : undefined,
    looksLikeIdToken: (claims.scp ?? claims.scope) === undefined && claims.aud !== undefined,
  };
}

/**
 * Axios instance configured for API requests.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth token and tenant/project context
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const authData = readPersistedAuthJson();
    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        const storedToken = parsed?.state?.token ?? parsed?.token ?? null;
        const ssoToken: string | null = await getSsoToken();
        // Use || (not ??): getSsoToken() can return an empty string when MSAL resolves
        // without an access token, and we must still fall back to the stored token.
        const token: string | null = ssoToken || storedToken;
        const tokenSource = ssoToken ? 'sso' : storedToken ? 'stored' : 'none';

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        if (import.meta.env.DEV) {
          console.debug('[api] → request auth', config.method?.toUpperCase(), config.url, {
            tokenSource,
            token: summarizeToken(token),
          });
        }

        if (!token && import.meta.env.DEV) {
          console.warn('[api] No token available for request', config.url, {
            hasStored: !!storedToken,
            storedToken: summarizeToken(storedToken),
          });
        }
      } catch (e) {
        if (import.meta.env.DEV) {
          console.warn('[api] Failed to parse auth storage for request', config.url, e);
        }
      }
    } else if (import.meta.env.DEV) {
      // No auth storage at all: interceptor will send request WITHOUT an Authorization header.
      console.warn('[api] No agentops-auth-storage in sessionStorage; request sent unauthenticated', config.method?.toUpperCase(), config.url);
    }

    // Prefer live Zustand context; fall back to persisted localStorage if needed
    let currentTenant = useContextStore.getState().currentTenant;
    let currentProject = useContextStore.getState().currentProject;

    if (!currentTenant?.id) {
      const contextData = localStorage.getItem('agentops-context-storage');
      if (contextData) {
        try {
          const parsed = JSON.parse(contextData);
          currentTenant = parsed?.state?.currentTenant ?? currentTenant;
          currentProject = parsed?.state?.currentProject ?? currentProject;
        } catch {
          // Ignore parse errors
        }
      }
    }

    if (currentTenant?.id) {
      config.headers['X-Tenant-Id'] = currentTenant.id;
    }
    if (currentProject?.id) {
      config.headers['X-Project-Id'] = currentProject.id;
    }

    // Default Content-Type: application/json breaks multipart uploads (415 on skills, etc.).
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else {
        delete (config.headers as Record<string, unknown>)['Content-Type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - clear auth and redirect to / (ProtectedRoute will trigger B2C login)
    if (error.response?.status === 401) {
      const skipAuthRedirect = (error.config as InternalAxiosRequestConfig & { skipAuthRedirect?: boolean })?.skipAuthRedirect;

      // Log rich diagnostics for the 401 BEFORE we clear auth storage below.
      // Logged in all environments because a 401 is an actionable error, not dev noise.
      const sentAuthHeader = (error.config?.headers?.Authorization ?? error.config?.headers?.authorization) as string | undefined;
      const sentToken = sentAuthHeader?.replace(/^Bearer\s+/i, '');
      console.warn('[api] 401 Unauthorized', error.config?.method?.toUpperCase(), error.config?.url, {
        skipAuthRedirect: !!skipAuthRedirect,
        // Why the server rejected us (B2C/JWT bearer sets this on 401).
        wwwAuthenticate: error.response?.headers?.['www-authenticate'],
        // Concrete backend token-validation failure (Development only; requires CORS exposed header).
        authErrorReason: error.response?.headers?.['x-auth-error-reason'],
        responseBody: error.response?.data,
        // Was an Authorization header actually attached, and what did the token look like?
        authHeaderPresent: !!sentAuthHeader,
        sentToken: summarizeToken(sentToken),
        sentTenantId: error.config?.headers?.['X-Tenant-Id'],
        sentProjectId: error.config?.headers?.['X-Project-Id'],
      });

      if (!skipAuthRedirect) {
        clearPersistedAuth();
        useContextStore.getState().clearContext();
        window.location.href = '/';
      }
      return Promise.reject(error);
    }

    // Suppress console errors for 500 errors when backend is unavailable
    // These are expected during development when backend isn't running
    // Only log in development mode for debugging
    if (error.response?.status === 500 && import.meta.env.DEV) {
      // Silently handle 500 errors - they're expected when backend isn't available
      // The calling code will handle the error appropriately
      console.debug('Backend API error (expected if backend is not running):', error.config?.url);
    }

    return Promise.reject(error);
  }
);
